// src/pages/bridges/BridgeDetailPage.tsx
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Cable,
  EthernetPort,
  Link2,
  Loader2,
  Network,
  Plus,
  RefreshCw,
  Router,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { Bridge } from "../../components/bridge/BridgeFormModal";
import type { BridgePort } from "../../components/bridge/BridgePortsTable";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

type TrafficSample = {
  timestamp: string;
  rx_bps: number;
  tx_bps: number;
};

const MAX_POINTS = 60;

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatBps = (bps: number): string => {
  const n = Number(bps);
  if (!Number.isFinite(n) || n <= 0) return "0 B/s";

  const units = ["B/s", "KB/s", "MB/s", "GB/s", "TB/s"];
  const k = 1024;
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(k)));
  const value = n / Math.pow(k, i);

  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[i]}`;
};

const MetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose" | "violet";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
    violet: {
      card: "border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 shadow-violet-200/50",
      icon: "bg-violet-100 text-violet-700",
      text: "text-violet-700",
      bubble: "bg-violet-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <div className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900">
            {value}
          </div>

          {sub && (
            <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const BridgeStatusBadge: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-black ${
      disabled
        ? "border-slate-300 bg-slate-200 text-slate-600"
        : "border-emerald-200 bg-emerald-100 text-emerald-800"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        disabled ? "bg-slate-500" : "bg-emerald-500"
      }`}
    />
    {disabled ? "Disabled" : "Active"}
  </span>
);

const BridgeDetailPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { routerId?: number } };
  const { showToast } = useToast();

  const bridgeName = decodeURIComponent(name || "");
  const routerId = location.state?.routerId;

  const [bridge, setBridge] = useState<Bridge | null>(null);
  const [loadingBridge, setLoadingBridge] = useState(false);

  const [trafficHistory, setTrafficHistory] = useState<TrafficSample[]>([]);

  const [ports, setPorts] = useState<BridgePort[]>([]);
  const [loadingPorts, setLoadingPorts] = useState(false);

  const [portForm, setPortForm] = useState<{ iface: string }>({ iface: "" });
  const [portFormLoading, setPortFormLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemovePort, setPendingRemovePort] = useState<string | null>(
    null,
  );
  const [removeLoading, setRemoveLoading] = useState(false);

  const resetError = () => setError(null);

  const fetchBridge = async () => {
    if (!bridgeName || routerId == null) return;

    try {
      setLoadingBridge(true);
      const data = await fetchApp<Bridge[]>(`/bridge?router_id=${routerId}`);
      const found = data.find((b) => b.name === bridgeName) || null;
      setBridge(found);
    } catch (err: any) {
      setError(err.message || "Failed to load bridge details");
    } finally {
      setLoadingBridge(false);
    }
  };

  const fetchPorts = async () => {
    if (!bridgeName || routerId == null) return;

    try {
      setLoadingPorts(true);

      const data = await fetchApp<BridgePort[]>(
        `/bridge/ports/list?router_id=${routerId}`,
      );

      setPorts(data.filter((p) => p.bridge === bridgeName));
    } catch (err: any) {
      setError(err.message || "Failed to load ports");
    } finally {
      setLoadingPorts(false);
    }
  };

  const fetchTrafficOnce = async () => {
    if (!bridgeName || routerId == null) return;

    try {
      const sample = await fetchApp<TrafficSample>(
        `/bridge/traffic?router_id=${routerId}&name=${encodeURIComponent(
          bridgeName,
        )}`,
      );

      setTrafficHistory((prev) => {
        const next = [...prev, sample];
        if (next.length > MAX_POINTS) next.shift();
        return next;
      });
    } catch (err: any) {
      console.error("Bridge traffic error:", err.message);
    }
  };

  const refreshAll = async () => {
    resetError();
    await Promise.all([fetchBridge(), fetchPorts()]);
    await fetchTrafficOnce();
  };

  useEffect(() => {
    if (routerId == null) {
      setError(
        "Missing routerId for this bridge. Please open it from the bridges list.",
      );
      return;
    }

    fetchBridge();
    fetchPorts();
    fetchTrafficOnce();

    const id = window.setInterval(fetchTrafficOnce, 3000);

    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgeName, routerId]);

  const handleAddPort = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bridgeName || routerId == null || !portForm.iface.trim()) return;

    try {
      setPortFormLoading(true);
      resetError();

      await fetchApp(`/bridge/port/add?router_id=${routerId}`, {
        method: "POST",
        body: JSON.stringify({
          bridge: bridgeName,
          interface: portForm.iface.trim(),
        }),
      });

      setPortForm({ iface: "" });
      showToast("Port added successfully", "success");
      await fetchPorts();
    } catch (err: any) {
      setError(err.message || "Failed to add port");
      showToast("Failed to add port", "error");
    } finally {
      setPortFormLoading(false);
    }
  };

  const initiateRemovePort = (iface: string) => {
    setPendingRemovePort(iface);
    setConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (routerId == null || !pendingRemovePort) return;

    setRemoveLoading(true);

    try {
      resetError();

      await fetchApp(
        `/bridge/port/remove/${encodeURIComponent(
          pendingRemovePort,
        )}?router_id=${routerId}`,
        { method: "DELETE" },
      );

      showToast(
        `Interface ${pendingRemovePort} removed from bridge`,
        "success",
      );
      await fetchPorts();
    } catch (err: any) {
      setError(err.message || "Failed to remove port");
      showToast("Failed to remove port", "error");
    } finally {
      setRemoveLoading(false);
      setConfirmOpen(false);
      setPendingRemovePort(null);
    }
  };

  const latestTraffic = trafficHistory[trafficHistory.length - 1];

  const bridgeDisabled =
    (bridge as any)?.disabled === true || (bridge as any)?.disabled === "yes";

  const isInitialLoading = loadingBridge && !bridge;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-[12.5px] font-bold text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Bridges
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Bridge Detail
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  <h1 className="text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                    {bridgeName || "Bridge"}
                  </h1>

                  {bridge && <BridgeStatusBadge disabled={bridgeDisabled} />}
                </div>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Monitor bridge traffic, view connected ports, and manage
                  member interfaces.
                </p>
              </div>

              <button
                type="button"
                onClick={refreshAll}
                disabled={loadingBridge || loadingPorts}
                className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {loadingBridge || loadingPorts ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="text-[13px] font-bold text-slate-500">
              Loading bridge details…
            </p>
          </div>
        ) : (
          <>
            {/* Metrics */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Router ID"
                value={routerId ?? "—"}
                sub="Source router"
                icon={<Router className="h-5 w-5" />}
                tone="slate"
              />

              <MetricCard
                label="Bridge Ports"
                value={ports.length}
                sub="Attached interfaces"
                icon={<Link2 className="h-5 w-5" />}
                tone="emerald"
              />

              <MetricCard
                label="Current RX"
                value={
                  latestTraffic ? formatBps(latestTraffic.rx_bps) : "0 B/s"
                }
                sub="Receive traffic"
                icon={<ArrowDown className="h-5 w-5" />}
                tone="sky"
              />

              <MetricCard
                label="Current TX"
                value={
                  latestTraffic ? formatBps(latestTraffic.tx_bps) : "0 B/s"
                }
                sub="Transmit traffic"
                icon={<ArrowUp className="h-5 w-5" />}
                tone="amber"
              />
            </div>

            {/* Bridge Info */}
            <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-5 shadow-lg shadow-slate-300/25">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                  <Network className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[14px] font-black text-slate-900">
                    Bridge Information
                  </p>

                  <p className="text-[11.5px] font-medium text-slate-500">
                    Router and bridge configuration summary.
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Bridge Name
                  </p>
                  <p className="mt-1 font-mono text-[13px] font-black text-slate-900">
                    {bridgeName}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Comment
                  </p>
                  <p className="mt-1 text-[13px] font-medium text-slate-700">
                    {bridge?.comment || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Traffic Chart */}
            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 shadow-xl shadow-slate-400/30">
              <div className="h-1.5 w-full bg-gradient-to-r from-cyan-400 to-amber-400" />

              <div className="p-4 sm:p-6">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="flex items-center gap-2 text-[15px] font-black text-white">
                      <Activity className="h-4 w-4 text-cyan-300" />
                      Real-time Traffic
                    </h2>

                    <p className="mt-1 text-[12px] font-medium text-slate-400">
                      Monitoring live bridge traffic for{" "}
                      <span className="font-mono font-bold text-white">
                        {bridgeName}
                      </span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 text-[11px] font-black">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-cyan-200">
                      <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.65)]" />
                      RX
                    </span>

                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-amber-200">
                      <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.65)]" />
                      TX
                    </span>
                  </div>
                </div>

                <div className="h-[260px] w-full sm:h-[310px]">
                  {trafficHistory.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-500">
                      <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-white/5">
                        <Activity className="h-8 w-8 text-slate-500" />
                      </div>

                      <p className="text-[13px] font-bold">
                        Waiting for traffic data…
                      </p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trafficHistory}>
                        <defs>
                          <linearGradient
                            id="rxGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#22d3ee"
                              stopOpacity={0.35}
                            />
                            <stop
                              offset="95%"
                              stopColor="#22d3ee"
                              stopOpacity={0}
                            />
                          </linearGradient>

                          <linearGradient
                            id="txGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#f59e0b"
                              stopOpacity={0.35}
                            />
                            <stop
                              offset="95%"
                              stopColor="#f59e0b"
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>

                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#1e293b"
                          vertical={false}
                        />

                        <XAxis dataKey="timestamp" hide />

                        <YAxis
                          tickFormatter={(value) =>
                            formatBps(Number(value)).replace("/s", "")
                          }
                          stroke="#64748b"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          width={64}
                        />

                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(2, 6, 23, 0.94)",
                            border: "1px solid rgba(51, 65, 85, 1)",
                            borderRadius: "14px",
                            fontSize: "12px",
                          }}
                          labelStyle={{ color: "#94a3b8" }}
                          labelFormatter={(ts) =>
                            new Date(ts as string).toLocaleTimeString()
                          }
                          formatter={(value: number, itemName: string) => [
                            <span
                              style={{
                                color:
                                  itemName === "rx_bps" ? "#67e8f9" : "#fbbf24",
                                fontWeight: 800,
                              }}
                            >
                              {formatBps(Number(value))}
                            </span>,
                            itemName === "rx_bps" ? "RX" : "TX",
                          ]}
                        />

                        <Area
                          type="monotone"
                          dataKey="rx_bps"
                          stroke="#22d3ee"
                          fill="url(#rxGradient)"
                          strokeWidth={2.5}
                          isAnimationActive={false}
                        />

                        <Area
                          type="monotone"
                          dataKey="tx_bps"
                          stroke="#f59e0b"
                          fill="url(#txGradient)"
                          strokeWidth={2.5}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* Ports */}
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
              <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <Cable className="h-5 w-5" />
                  </div>

                  <div>
                    <p className="text-[14px] font-black text-slate-900">
                      Bridge Ports
                    </p>

                    <p className="text-[11.5px] font-medium text-slate-500">
                      Interfaces connected to this bridge.
                    </p>
                  </div>
                </div>

                <form
                  onSubmit={handleAddPort}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center"
                >
                  <div className="relative w-full sm:w-64">
                    <EthernetPort className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      value={portForm.iface}
                      onChange={(e) => setPortForm({ iface: e.target.value })}
                      placeholder="Interface, e.g. ether2"
                      className={`${inputCls} pl-10`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={portFormLoading || !portForm.iface.trim()}
                    className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-4 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                  >
                    {portFormLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    Add Port
                  </button>
                </form>
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="px-5 py-3.5 font-black">Interface</th>
                      <th className="px-5 py-3.5 font-black">Bridge</th>
                      <th className="px-5 py-3.5 text-right font-black">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                    {loadingPorts ? (
                      <tr>
                        <td colSpan={3} className="px-5 py-12 text-center">
                          <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-500" />
                          <p className="mt-2 text-[13px] font-bold text-slate-500">
                            Loading ports…
                          </p>
                        </td>
                      </tr>
                    ) : ports.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-5 py-14 text-center">
                          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                            <Link2 className="h-7 w-7 text-slate-500" />
                          </div>

                          <p className="text-[14px] font-black text-slate-800">
                            No ports attached
                          </p>

                          <p className="mt-1 text-[13px] font-medium text-slate-500">
                            Add an interface to connect it to this bridge.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      ports.map((port) => (
                        <tr
                          key={`${port.bridge}-${port.interface}`}
                          className="transition-colors hover:bg-emerald-50/60"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                                <EthernetPort className="h-4 w-4" />
                              </div>

                              <p className="font-mono text-[13px] font-black text-slate-900">
                                {port.interface}
                              </p>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <span className="inline-flex rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                              {port.bridge}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => initiateRemovePort(port.interface)}
                              className={`${btnBase} border border-rose-200 bg-rose-50 px-3 py-2 text-rose-600 shadow-sm hover:bg-rose-100`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
                {loadingPorts ? (
                  <div className="px-4 py-12 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-500" />
                    <p className="mt-2 text-[13px] font-bold text-slate-500">
                      Loading ports…
                    </p>
                  </div>
                ) : ports.length === 0 ? (
                  <div className="px-4 py-14 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                      <Link2 className="h-7 w-7 text-slate-500" />
                    </div>

                    <p className="text-[14px] font-black text-slate-800">
                      No ports attached
                    </p>

                    <p className="mt-1 text-[13px] font-medium text-slate-500">
                      Add an interface to connect it to this bridge.
                    </p>
                  </div>
                ) : (
                  ports.map((port) => (
                    <div
                      key={`${port.bridge}-${port.interface}`}
                      className="px-4 py-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                          <EthernetPort className="h-4 w-4" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-[13.5px] font-black text-slate-900">
                            {port.interface}
                          </p>

                          <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                            Bridge: {port.bridge}
                          </p>

                          <button
                            type="button"
                            onClick={() => initiateRemovePort(port.interface)}
                            className={`${btnBase} mt-3 h-10 w-full border border-rose-200 bg-rose-50 text-rose-600 shadow-sm hover:bg-rose-100`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove Port
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        loading={removeLoading}
        title="Remove Port"
        description={`Are you sure you want to remove interface "${pendingRemovePort}" from the bridge? Traffic on this interface will no longer be bridged.`}
        confirmLabel="Remove Port"
        variant="danger"
        onCancel={() => {
          if (!removeLoading) {
            setConfirmOpen(false);
            setPendingRemovePort(null);
          }
        }}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default BridgeDetailPage;
